/**
 * SWISS School Portal - Authentication Context
 * =============================================
 * Two sign-in paths:
 *   1. Username/password — schools, JWT issued by portal API.
 *   2. Cognito Hosted UI with Google federation — internal staff.
 *      Authorization code flow with PKCE; tokens are exchanged
 *      from the /auth/callback page via cognitoAuth helpers.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { config } from '../config/config';
import {
  signInWithGoogle as cognitoSignInWithGoogle,
  signInWithEmail as cognitoSignInWithEmail,
  exchangeCodeForTokens,
  signInWithCredentials,
  cognitoLogout,
} from '../services/cognitoAuth';
import { getMe, recordLogin, resetSessionExpiryGuard } from '../services/adminApi';
import { recordAuthLogFailure } from '../services/authLog';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const ADMIN_PERMISSIONS = {
  viewProgress: true,
  viewTeachers: true,
  viewCouncil: true,
  viewCrisis: true,
  reportCrisis: true,
  manageSettings: true,
};

const STAFF_PERMISSIONS = {
  viewProgress: true,
  viewCrisis: true,
};

const isSuperAdminEmail = (email) =>
  !!email && config.superAdminEmails.map((e) => e.toLowerCase()).includes(email.toLowerCase());

const isMssAdminEmail = (email) =>
  !!email && (config.mssAdminEmails || []).map((e) => e.toLowerCase()).includes(email.toLowerCase());

const isDomainAllowed = (email) => {
  if (!email) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return !!domain && config.google.allowedDomains.includes(domain);
};

const extractGroups = (payload) => {
  const g = payload['cognito:groups'];
  if (!g) return [];
  if (Array.isArray(g)) return g;
  if (typeof g === 'string') return g.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
};

const buildCognitoUser = (idTokenPayload) => {
  const email = idTokenPayload.email;
  const groups = extractGroups(idTokenPayload);
  const isSchoolAdmin = groups.includes('school_admin');
  const isSystemAdmin = groups.includes('system_admin');
  // 3rd-party physical-safety auditor — restricted to the Physical
  // Audit module for their assigned schools.
  const isAuditor = !isSchoolAdmin && !isSystemAdmin && groups.includes('auditor');
  const isSA = !isSchoolAdmin && !isSystemAdmin && !isAuditor && isSuperAdminEmail(email);
  const isMSS = !isSchoolAdmin && !isSystemAdmin && !isAuditor && isMssAdminEmail(email);
  const isAdmin = isSA || isMSS;
  const role =
    isSchoolAdmin ? 'school_admin' :
    isSystemAdmin ? 'system_admin' :
    isAuditor ? 'auditor' :
    isSA ? 'superadmin' :
    isMSS ? 'mss_admin' : 'staff';
  return {
    sub: idTokenPayload.sub,
    email,
    name: idTokenPayload.name || idTokenPayload['cognito:username'] || email,
    givenName: idTokenPayload.given_name,
    familyName: idTokenPayload.family_name,
    picture: idTokenPayload.picture,
    authProvider: 'cognito',
    isSuperAdmin: isSA,
    isMssAdmin: isMSS,
    isSchoolAdmin,
    isSystemAdmin,
    isAuditor,
    isAdmin,
    role,
    groups,
    permissions: isAdmin ? ADMIN_PERMISSIONS : STAFF_PERMISSIONS,
  };
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(config.jwt.storageKey));
  const [school, setSchool] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Admin impersonation: when an MSS admin clicks "View as school" on a
  // school they manage, this becomes the effective school. Cleared when
  // they exit the preview. Never written to localStorage.
  const [viewAsSchool, setViewAsSchool] = useState(null);

  // Decode and validate token (checks exp)
  const validateToken = useCallback((tokenStr) => {
    if (!tokenStr) return null;
    try {
      const decoded = jwtDecode(tokenStr);
      if (decoded.exp * 1000 < Date.now()) {
        console.log('Token expired');
        return null;
      }
      return decoded;
    } catch (err) {
      console.error('Token decode error:', err);
      return null;
    }
  }, []);

  // Restore session from storage
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      const storedToken = localStorage.getItem(config.jwt.storageKey);
      if (!storedToken) {
        setLoading(false);
        return;
      }

      const decoded = validateToken(storedToken);
      if (!decoded) {
        // Invalid/expired — clear
        localStorage.removeItem(config.jwt.storageKey);
        localStorage.removeItem(config.jwt.schoolStorageKey);
        localStorage.removeItem(config.jwt.refreshTokenKey);
        localStorage.removeItem(config.jwt.googleUserKey);
        setToken(null);
        setSchool(null);
        setUser(null);
        setLoading(false);
        return;
      }

      setToken(storedToken);

      // Cognito-issued id tokens carry `token_use: "id"` and an `iss` from Cognito
      const isCognitoToken = decoded.token_use === 'id' ||
        (decoded.iss && decoded.iss.includes('cognito-idp'));

      if (!isCognitoToken) {
        // School/password session
        setSchool({
          id: decoded.schoolId,
          name: decoded.schoolName,
          shortName: decoded.shortName,
          tier: decoded.tier,
        });
        setUser({
          username: decoded.username,
          schoolId: decoded.schoolId,
          permissions: decoded.permissions || {},
        });
        setLoading(false);
        return;
      }

      const cognitoUser = buildCognitoUser(decoded);

      // Restore enriched user (with systemId for system_admin) from
      // storage so the first render after refresh already has it.
      const cachedUser = localStorage.getItem(config.jwt.googleUserKey);
      let initialUser = cognitoUser;
      if (cachedUser) {
        try {
          const parsed = JSON.parse(cachedUser);
          if (parsed?.email === cognitoUser.email && parsed.systemId) {
            initialUser = { ...cognitoUser, systemId: parsed.systemId, system: parsed.system };
          } else if (parsed?.email === cognitoUser.email && parsed.isAuditor && parsed.schools) {
            initialUser = {
              ...cognitoUser,
              auditorId: parsed.auditorId || null,
              schoolIds: parsed.schoolIds || [],
              schools: parsed.schools || [],
            };
          }
        } catch { /* fall through to plain cognitoUser */ }
      }
      setUser(initialUser);

      // Record this session in the Activity log too. recordLogin
      // dedupes per-tab via sessionStorage, so the fresh-sign-in path
      // (which already called this in completeCognitoSignIn) is a
      // no-op here; reopening Safari with a cached token (which skips
      // completeCognitoSignIn entirely) gets counted.
      recordLogin();

      if (cognitoUser.isSchoolAdmin) {
        const storedSchool = localStorage.getItem(config.jwt.schoolStorageKey);
        if (storedSchool) {
          try { setSchool(JSON.parse(storedSchool)); } catch { /* ignore */ }
        }
        // Refresh school in the background — don't block loading.
        getMe()
          .then((me) => {
            if (cancelled) return;
            if (me?.school) {
              localStorage.setItem(config.jwt.schoolStorageKey, JSON.stringify(me.school));
              setSchool(me.school);
            }
          })
          .catch(() => { /* keep cached school */ });
        setLoading(false);
      } else if (cognitoUser.isSystemAdmin) {
        setSchool(null);
        if (!initialUser.systemId) {
          // No cached systemId — AWAIT getMe before declaring loading
          // done so the route guard can pick the right destination.
          // Without this, the user briefly lands on /dashboard (school
          // portal) before the redirect fires once getMe resolves.
          try {
            const me = await getMe();
            if (!cancelled) {
              if (me?.system) {
                const enriched = { ...cognitoUser, systemId: me.systemId, system: me.system };
                localStorage.setItem(config.jwt.googleUserKey, JSON.stringify(enriched));
                setUser(enriched);
              } else {
                // Server replied but no system attached — log loudly so
                // we can diagnose. Likely causes: deleted network row,
                // group mismatch, stale invite.
                console.error('[AuthContext] /admin/me returned no system for', cognitoUser.email, me);
              }
            }
          } catch (e) {
            console.error('[AuthContext] /admin/me failed during session restore for',
              cognitoUser.email,
              { message: e.message, status: e.status, body: e.body });
          }
        } else {
          // Have a cached systemId — refresh in background.
          getMe()
            .then((me) => {
              if (cancelled || !me?.system) return;
              const enriched = { ...cognitoUser, systemId: me.systemId, system: me.system };
              localStorage.setItem(config.jwt.googleUserKey, JSON.stringify(enriched));
              setUser(enriched);
            })
            .catch(() => { /* keep cached */ });
        }
        setLoading(false);
      } else if (cognitoUser.isAuditor) {
        // 3rd-party auditor — await getMe so the assigned schools are
        // in hand before the first render.
        setSchool(null);
        try {
          const me = await getMe();
          if (!cancelled && me?.role === 'auditor') {
            const enriched = {
              ...cognitoUser,
              auditorId: me.auditorId || null,
              schoolIds: me.schoolIds || [],
              schools: me.schools || [],
            };
            localStorage.setItem(config.jwt.googleUserKey, JSON.stringify(enriched));
            setUser(enriched);
          }
        } catch (e) {
          console.warn('[AuthContext] auditor session restore failed', e);
        }
        setLoading(false);
      } else {
        setSchool(null);
        setLoading(false);
      }
    };

    restore();
    return () => { cancelled = true; };
  }, [validateToken]);

  // Listen for session-expired events from adminApi. When a backend
  // call comes back 401, apiFetch fires this event so we can clean
  // up local state and send the user to /login with a friendly
  // "session expired" banner instead of letting a raw 401 error
  // bubble up into whatever page they were on.
  useEffect(() => {
    const onExpired = () => {
      console.log('[AuthContext] session expired — clearing local state');
      localStorage.removeItem(config.jwt.storageKey);
      localStorage.removeItem(config.jwt.schoolStorageKey);
      localStorage.removeItem(config.jwt.refreshTokenKey);
      localStorage.removeItem(config.jwt.googleUserKey);
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('swiss_login_recorded');
      }
      setToken(null);
      setUser(null);
      setSchool(null);
      setViewAsSchool(null);
      // Hard navigation so any in-flight React Query work and stale
      // page state gets dropped on the floor. The /login page itself
      // reads the sessionStorage flag adminApi set and shows the
      // banner.
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    };
    window.addEventListener('swiss:session-expired', onExpired);
    return () => window.removeEventListener('swiss:session-expired', onExpired);
  }, []);

  // Email/password login (school portal API). School admins/staff
  // sign in with their email address as the identifier.
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    let responseStatus = null;

    try {
      const response = await fetch(`${config.api.portalUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      responseStatus = response.status;

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      if (data.success && data.token) {
        localStorage.setItem(config.jwt.storageKey, data.token);
        localStorage.setItem(config.jwt.schoolStorageKey, JSON.stringify(data.school));

        const decoded = jwtDecode(data.token);
        setToken(data.token);
        setSchool(data.school);
        setUser({
          username: decoded.username,
          schoolId: decoded.schoolId,
          permissions: decoded.permissions || {},
        });

        return { success: true };
      } else {
        throw new Error(data.error || 'Login failed');
      }
    } catch (err) {
      setError(err.message);
      recordAuthLogFailure({ identifier: email, reason: err.message, status: responseStatus, path: '/auth/login' });
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Start the Cognito Hosted UI redirect with Google as the IdP
  const signInWithGoogle = useCallback(async () => {
    setError(null);
    try {
      await cognitoSignInWithGoogle();
    } catch (err) {
      console.error('Failed to start Cognito sign-in:', err);
      setError('Failed to start Google sign-in. Please try again.');
    }
  }, []);

  // Start the Cognito Hosted UI redirect using the native email/password form.
  // Used by school admins.
  const signInWithEmail = useCallback(async () => {
    setError(null);
    try {
      await cognitoSignInWithEmail();
    } catch (err) {
      console.error('Failed to start Cognito email sign-in:', err);
      setError('Failed to start email sign-in. Please try again.');
    }
  }, []);

  /**
   * Apply a freshly-issued Cognito token set: build the user, store
   * tokens, resolve the school/system context via getMe(). Used by
   * both the OAuth-callback path and the demo "Sign in as …" buttons.
   */
  const persistTokensAndResolveUser = useCallback(async (tokens, { strictDomainCheck = true } = {}) => {
    const decoded = jwtDecode(tokens.id_token);
    let cognitoUser = buildCognitoUser(decoded);

    const domainOk = cognitoUser.isSchoolAdmin || cognitoUser.isSystemAdmin
      || cognitoUser.isAuditor || isDomainAllowed(decoded.email);

    // Store tokens before any getMe() call below — adminApi reads the id
    // token straight out of localStorage. Rolled back if the account gets
    // rejected further down.
    localStorage.setItem(config.jwt.storageKey, tokens.id_token);
    if (tokens.refresh_token) {
      localStorage.setItem(config.jwt.refreshTokenKey, tokens.refresh_token);
    }

    if (!cognitoUser.isSchoolAdmin && !cognitoUser.isSystemAdmin && !cognitoUser.isAuditor) {
      // config.superAdminEmails/mssAdminEmails (config.js) and the domain
      // allowlist are only a fast local pre-check. TABLES.admins — kept up
      // to date by the "Invite Admin" UI — is the real source of truth for
      // invited MSS admins/superadmins, including ones this static list
      // hasn't been updated for yet, or on a domain it doesn't recognize.
      // Ask the backend for the authoritative role before finalizing admin
      // status (or, for a disallowed domain, before rejecting outright).
      let backendRole = null;
      try {
        const me = await getMe();
        backendRole = me?.role || null;
      } catch { /* not authorized per backend — falls through below */ }

      if (backendRole === 'superadmin' || backendRole === 'mss_admin') {
        const isSA = backendRole === 'superadmin';
        cognitoUser = {
          ...cognitoUser,
          isSuperAdmin: isSA,
          isMssAdmin: !isSA,
          isAdmin: true,
          role: backendRole,
          permissions: ADMIN_PERMISSIONS,
        };
      } else if (strictDomainCheck && !domainOk) {
        localStorage.removeItem(config.jwt.storageKey);
        localStorage.removeItem(config.jwt.refreshTokenKey);
        const msg = 'This Google account isn’t a My Safe Spaces staff account. '
          + 'If you are a school administrator, go back and use “Sign in with Email” '
          + 'with the email and password from your invitation.';
        setError(msg);
        recordAuthLogFailure({ identifier: decoded.email, reason: 'Google account not in allowed staff domain', path: '/auth/callback' });
        return { success: false, error: msg };
      }
    }

    localStorage.setItem(config.jwt.googleUserKey, JSON.stringify(cognitoUser));

    setToken(tokens.id_token);
    setUser(cognitoUser);
    resetSessionExpiryGuard();
    recordLogin();

    if (cognitoUser.isSchoolAdmin) {
      try {
        const me = await getMe();
        if (me?.school) {
          localStorage.setItem(config.jwt.schoolStorageKey, JSON.stringify(me.school));
          setSchool(me.school);
        } else { setSchool(null); }
      } catch (e) {
        console.warn('school_admin: getMe failed', e);
        setSchool(null);
      }
    } else if (cognitoUser.isSystemAdmin) {
      try {
        const me = await getMe();
        if (me?.system) {
          const enriched = { ...cognitoUser, systemId: me.systemId, system: me.system };
          localStorage.setItem(config.jwt.googleUserKey, JSON.stringify(enriched));
          setUser(enriched);
          return { success: true, role: cognitoUser.role, systemId: me.systemId };
        }
        setSchool(null);
      } catch (e) {
        console.warn('system_admin: getMe failed', e);
        setSchool(null);
      }
    } else if (cognitoUser.isAuditor) {
      try {
        const me = await getMe();
        const enriched = {
          ...cognitoUser,
          auditorId: me?.auditorId || null,
          schoolIds: me?.schoolIds || [],
          schools: me?.schools || [],
        };
        localStorage.setItem(config.jwt.googleUserKey, JSON.stringify(enriched));
        setUser(enriched);
        setSchool(null);
        return { success: true, role: 'auditor' };
      } catch (e) {
        console.warn('auditor: getMe failed', e);
        setSchool(null);
      }
    } else {
      localStorage.removeItem(config.jwt.schoolStorageKey);
      setSchool(null);
    }
    return { success: true, role: cognitoUser.role };
  }, []);

  /**
   * "Sign in as <role>" one-click demo entry. Uses Cognito's
   * USER_PASSWORD_AUTH directly so no Hosted UI redirect is needed.
   * Three demo accounts are pre-seeded with password Swiss2026!.
   *   - 'group'      → groupadmin@swiss.test    (Demo Group, 3 schools)
   *   - 'school'     → testadmin@swiss.test     (Test School, aggregate only)
   *   - 'counsellor' → counsellor@swiss.test    (Test School, per-student access)
   */
  const demoLoginAs = useCallback(async (role) => {
    setError(null);
    const ACCOUNTS = {
      group:      { email: 'groupadmin@swiss.test', password: 'Swiss2026!' },
      school:     { email: 'testadmin@swiss.test',  password: 'Swiss2026!' },
      counsellor: { email: 'counsellor@swiss.test', password: 'Swiss2026!' },
    };
    const cfg = ACCOUNTS[role];
    if (!cfg) {
      setError(`Unknown demo role: ${role}`);
      return { success: false };
    }
    try {
      const tokens = await signInWithCredentials(cfg);
      // strictDomainCheck=false: the demo accounts use the @swiss.test
      // host which isn't in the MSS staff domain list — they're
      // school_admin / system_admin invitees, not Google federation.
      return await persistTokensAndResolveUser(tokens, { strictDomainCheck: false });
    } catch (err) {
      console.error('Demo sign-in failed:', err);
      const msg = err.message || 'Demo sign-in failed.';
      setError(msg);
      recordAuthLogFailure({ identifier: cfg.email, reason: msg, path: '/login' });
      return { success: false, error: msg };
    }
  }, [persistTokensAndResolveUser]);

  // Called by the /auth/callback page after Cognito redirects back
  const completeCognitoSignIn = useCallback(async ({ code, state }) => {
    setError(null);
    try {
      const tokens = await exchangeCodeForTokens({ code, state });
      return await persistTokensAndResolveUser(tokens);
    } catch (err) {
      console.error('Cognito callback failed:', err);
      setError(err.message || 'Sign-in failed.');
      // No identifier available here — the token exchange itself
      // failed (expired/reused code, CSRF-state mismatch), so we never
      // got as far as decoding an id_token.
      recordAuthLogFailure({ identifier: null, reason: err.message || 'Cognito token exchange failed', path: '/auth/callback' });
      return { success: false, error: err.message };
    }
  }, [persistTokensAndResolveUser]);

  // Demo login (kept for QA/preview)
  const demoLogin = async () => {
    setLoading(true);

    const mockSchool = {
      id: 'sch_001',
      name: 'Delhi Public School, Gurgaon',
      shortName: 'DPS Gurgaon',
      tier: 'Premium',
    };

    // Demo token shaped like a real 3-part JWT so jwtDecode can read the
    // payload on page reload. Signature is not validated locally.
    const b64url = (s) =>
      btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    const payload = {
      sub: mockSchool.id,
      schoolId: mockSchool.id,
      schoolName: mockSchool.name,
      shortName: mockSchool.shortName,
      username: 'dps_demo',
      tier: mockSchool.tier,
      permissions: {
        viewProgress: true,
        viewTeachers: true,
        viewCouncil: true,
        viewCrisis: true,
        reportCrisis: true,
      },
      exp: Math.floor(Date.now() / 1000) + 86400,
      iat: Math.floor(Date.now() / 1000),
    };
    const mockToken = `${b64url(JSON.stringify({ alg: 'none', typ: 'JWT' }))}.${b64url(JSON.stringify(payload))}.demo`;

    localStorage.setItem(config.jwt.storageKey, mockToken);
    localStorage.setItem(config.jwt.schoolStorageKey, JSON.stringify(mockSchool));

    setToken(mockToken);
    setSchool(mockSchool);
    setUser({
      username: 'dps_demo',
      schoolId: mockSchool.id,
      permissions: {
        viewProgress: true,
        viewTeachers: true,
        viewCouncil: true,
        viewCrisis: true,
        reportCrisis: true,
      },
    });

    setLoading(false);
    return { success: true };
  };

  // Admin impersonation helpers.
  const enterSchoolView = useCallback((schoolToView) => {
    if (!schoolToView?.id) return;
    setViewAsSchool(schoolToView);
  }, []);

  const exitSchoolView = useCallback(() => {
    setViewAsSchool(null);
  }, []);

  // Logout — clears local state. For Cognito sessions, also bounce through
  // Cognito's /logout so the federated cookie is cleared.
  const logout = ({ federated = true } = {}) => {
    setViewAsSchool(null);
    const wasCognito = user?.authProvider === 'cognito-google';

    localStorage.removeItem(config.jwt.storageKey);
    localStorage.removeItem(config.jwt.schoolStorageKey);
    localStorage.removeItem(config.jwt.refreshTokenKey);
    localStorage.removeItem(config.jwt.googleUserKey);
    // Clear the per-tab "login already recorded" flag so a sign-in
    // immediately after sign-out gets recorded again.
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('swiss_login_recorded');
    }
    setToken(null);
    setSchool(null);
    setUser(null);
    setError(null);

    if (wasCognito && federated) {
      cognitoLogout();
    }
  };

  const hasPermission = (permission) => {
    if (user?.isSuperAdmin) return true;
    return user?.permissions?.[permission] === true;
  };

  // Effective school: when an admin is impersonating, all school-scoped
  // pages should see the school they're previewing. The real `school`
  // remains accessible as `actualSchool` for the impersonation banner.
  const effectiveSchool = viewAsSchool || school;

  const value = {
    token,
    school: effectiveSchool,
    actualSchool: school,
    user,
    loading,
    error,
    viewAsSchool,
    // Authenticated when a token is in hand AND we recognise the identity:
    //   (a) school session (school admin or staff)
    //   (b) MSS / super admin (isAdmin)
    //   (c) school_admin (group-driven, school may not be loaded yet)
    //   (d) system_admin (manages a whole system, never has a single school)
    //   (e) admin previewing a school
    // Forgetting (d) silently bounced system admins to /login because the
    // route guard treated them as unauthenticated.
    isAuthenticated: !!token && (
      !!school
      || !!user?.isAdmin
      || !!user?.isSchoolAdmin
      || !!user?.isSystemAdmin
      || !!user?.isAuditor
      || !!viewAsSchool
    ),
    isSuperAdmin: !!user?.isSuperAdmin,
    isMssAdmin: !!user?.isMssAdmin,
    isAdmin: !!user?.isAdmin,
    isSchoolAdmin: !!user?.isSchoolAdmin,
    isSystemAdmin: !!user?.isSystemAdmin,
    isAuditor: !!user?.isAuditor,
    systemId: user?.systemId || null,
    login,
    signInWithGoogle,
    signInWithEmail,
    completeCognitoSignIn,
    demoLogin,
    demoLoginAs,
    logout,
    hasPermission,
    enterSchoolView,
    exitSchoolView,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
